import httpService from '@Utils/httpService';

const baseUrl = 'api/tagsRegisters';

export default {
  /**
   * Get tags by type with pagination and filtering
   * @param {string} tagType - Type of tags (Equipment, Pipeline, Structural, etc.)
   * @param {string} assetId - Asset ID from route parameters
   * @param {Object} params - Query parameters for filtering, sorting, pagination
   * @param {AbortSignal} signal - Optional cancellation signal
   * @returns {Promise} Tags data with pagination info
   */
  getTagsByType(tagType, assetId, params = {}, signal = null) {
    const requestParams = {
      page: params.page || 1,
      itemsPerPage: params.itemsPerPage || 15,
      search: params.search || '',
      sortBy: params.sortBy || 'name',
      sortDesc: params.sortDesc || false,
      linkStatus: params.linkStatus || ['Linked', 'Other'],
    };

    // Add column filters if they exist
    if (params.columnFilters && Object.keys(params.columnFilters).length > 0) {
      requestParams.columnFilters = params.columnFilters;
    }

    return httpService.get({
      url: `${baseUrl}/${assetId}/${tagType}`,
      params: requestParams,
      signal
    });
  },

  /**
   * Update a specific field of a tag (for inline editing)
   * @param {string} tagType - Type of tag
   * @param {string} assetId - Asset ID from route parameters
   * @param {number|string} tagId - Tag ID
   * @param {string} field - Field name to update
   * @param {any} value - New value for the field
   * @returns {Promise} Updated tag data
   */
  updateTagField(tagType, assetId, tagId, field, value) {
    return httpService.patch({
      url: `${baseUrl}/${assetId}/${tagType}/${tagId}/field`,
      data: {
        field,
        value
      }
    });
  },

  /**
   * Update multiple fields of a single tag (for row editing)
   * @param {string} tagType - Type of tag
   * @param {string} assetId - Asset ID from route parameters
   * @param {number|string} tagId - Tag ID
   * @param {Object} changes - Object with cells array containing {ColumnTitle, Value} objects
   * @returns {Promise} Updated tag data
   */
  updateTagMultipleFields(tagType, assetId, tagId, changes) {
    return httpService.patch({
      url: `${baseUrl}/${assetId}/${tagType}/${tagId}/fields`,
      data: changes
    });
  },

  /**
   * Delete a tag
   * @param {string} tagType - Type of tag
   * @param {string} assetId - Asset ID from route parameters
   * @param {number|string} tagId - Tag ID
   * @returns {Promise} Deletion confirmation
   */
  deleteTag(tagType, assetId, tagId) {
    return httpService.delete({
      url: `${baseUrl}/${assetId}/${tagType}/${tagId}`
    });
  },

  /**
   * Get column filter values for select filters
   * @param {string} tagType - Type of tag (Equipment, Pipeline, Structural, etc.)
   * @param {string} assetId - Asset ID from route parameters
   * @param {string} columnName - Column name to get values for
   * @returns {Promise} Array of unique column values
   */
  getColumnFilterValues(tagType, assetId, columnName) {
    const params = { columnName };
    return httpService.get({
      url: `${baseUrl}/${assetId}/${tagType}/column-values`,
      params
    });
  }
};
